function getMessage(e) {
    const r = typeof e === 'object' &&
        e !== null &&
        'message' in e &&
        typeof e.message === 'string'
        ? e.message
        : `${e}`;
    return r.replace(/\.$/, '');
}
export default class RemoteFile {
    constructor(source, opts = {}) {
        this.url = source;
        this.baseHeaders = opts.headers ?? {};
        this.baseOverrides = opts.overrides ?? {};
        this.fetchImplementation =
            opts.fetch ??
                ((input, init) => globalThis.fetch(input, init));
    }
    async fetch(input, init) {
        const wrapError = (e) => new Error(`${getMessage(e)} fetching ${input}`, { cause: e });
        let response;
        try {
            response = await this.fetchImplementation(input, init);
        }
        catch (e) {
            if (`${e}`.includes('Failed to fetch')) {
                // refetch to help work around a chrome bug (discussed in
                // generic-filehandle issue #72) in which the chrome cache returns a
                // CORS error for content in its cache.  see also
                // https://github.com/GMOD/jbrowse-components/pull/1511
                console.warn(`generic-filehandle: refetching ${input} to attempt to work around chrome CORS header caching bug`);
                try {
                    response = await this.fetchImplementation(input, {
                        ...init,
                        cache: 'reload',
                    });
                }
                catch (e) {
                    throw wrapError(e);
                }
            }
            else {
                throw wrapError(e);
            }
        }
        return response;
    }
    async read(length, position, opts = {}) {
        if (length === 0) {
            return new Uint8Array(0);
        }
        if (Number.isNaN(length) || Number.isNaN(position)) {
            throw new TypeError(`read() called with NaN length or position (length=${length}, position=${position}). The index file may be corrupt.`);
        }
        const { headers = {}, signal, overrides = {} } = opts;
        headers.range = `bytes=${position}-${position + length - 1}`;
        const res = await this.fetch(this.url, {
            ...this.baseOverrides,
            ...overrides,
            headers: { ...this.baseHeaders, ...headers },
            method: 'GET',
            redirect: 'follow',
            mode: 'cors',
            signal,
        });
        if (!res.ok) {
            throw new Error(`HTTP ${res.status} fetching ${this.url}`);
        }
        if ((res.status === 200 && position === 0) || res.status === 206) {
            // try to parse out the size of the remote file
            const contentRange = res.headers.get('content-range');
            const sizeMatch = /\/(\d+)$/.exec(contentRange ?? '');
            if (sizeMatch?.[1]) {
                this._stat = {
                    size: parseInt(sizeMatch[1], 10),
                };
            }
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            const resData = res.bytes
                ? await res.bytes()
                : new Uint8Array(await res.arrayBuffer());
            return resData.byteLength <= length
                ? resData
                : resData.subarray(0, length);
        }
        throw new Error(res.status === 200
            ? `${this.url} fetch returned status 200, expected 206`
            : `HTTP ${res.status} fetching ${this.url}`);
    }
    async readFile(options = {}) {
        const encoding = typeof options === 'string' ? options : options.encoding;
        const opts = typeof options === 'string' ? {} : options;
        const { headers = {}, signal, overrides = {} } = opts;
        const res = await this.fetch(this.url, {
            ...this.baseOverrides,
            ...overrides,
            headers: { ...this.baseHeaders, ...headers },
            method: 'GET',
            redirect: 'follow',
            mode: 'cors',
            signal,
        });
        if (!res.ok) {
            throw new Error(`HTTP ${res.status} fetching ${this.url}`);
        }
        if (encoding === 'utf8') {
            return res.text();
        }
        else if (encoding) {
            throw new Error(`unsupported encoding: ${encoding}`);
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            return res.bytes ? res.bytes() : new Uint8Array(await res.arrayBuffer());
        }
    }
    async stat() {
        if (!this._stat) {
            await this.read(10, 0);
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (!this._stat) {
                throw new Error(`unable to determine size of file at ${this.url}`);
            }
        }
        return this._stat;
    }
    close() {
        return Promise.resolve();
    }
}
//# sourceMappingURL=remoteFile.js.map